import type { IOwner, IOwnerPhone } from '@features/account/types'
import { convertIso2ToIso3 } from '@utils/formattingUtils'
import { DEFAULT_ACCOUNT_COUNTRY_CODE, parseStoredPhoneToNationalNumber, reconstructFullPhoneNumber } from './preprocessAccountPhone'

/**
 * What we expect to find on the owner after a profile-save round-trip.
 * Subset of `IOwner` plus the three phone slots — only the fields the My Account
 * edit form can change. Anything else is ignored by the verifier.
 */
export interface IExpectedProfileSnapshot {
  emailAddress?: string
  addressLine1?: string
  addressLine2?: string
  addressLine3?: string
  city?: string
  /** Country code (USA / CAN / etc). */
  country?: string
  /** Full state name as Voyager stores it (e.g. FLORIDA). */
  stateProvince?: string
  postalCode?: string
  phoneHome?: { phoneNumber: string; countryCode: string } | undefined
  phoneMobile?: { phoneNumber: string; countryCode: string } | undefined
  phoneWork?: { phoneNumber: string; countryCode: string } | undefined
}

export type TProfileSaveMismatchField =
  | 'emailAddress'
  | 'addressLine1'
  | 'addressLine2'
  | 'addressLine3'
  | 'city'
  | 'country'
  | 'stateProvince'
  | 'postalCode'
  | 'phoneHome'
  | 'phoneMobile'
  | 'phoneWork'
  /**
   * Sentinel: the post-save GET failed (network error, mkback 5xx, etc.) so we could not check
   * any field. Distinct from a real per-field mismatch so callers / logs do not misattribute
   * the failure to a specific field.
   */
  | '__profileFetchFailed'

export interface IProfileSaveMismatch {
  field: TProfileSaveMismatchField
  expected: string
  actual: string
}

const normalizeText = (value: string | null | undefined): string =>
  (value ?? '').replace(/\s+/g, ' ').trim().toUpperCase()

/**
 * Return phone digits in a comparable form: national digits only, no `+1` /
 * country prefix. Empty input → '' (representing "no phone for this slot").
 */
const normalizePhoneDigits = (
  phone: { phoneNumber?: string | null; countryCode?: string | null } | string | undefined | null,
): string => {
  if (phone == null) return ''
  if (typeof phone === 'string') {
    return reconstructFullPhoneNumber(phone, DEFAULT_ACCOUNT_COUNTRY_CODE)
  }
  const cc = phone.countryCode || DEFAULT_ACCOUNT_COUNTRY_CODE
  const number = phone.phoneNumber || ''
  if (!number) return ''
  // First strip the country prefix if present (Voyager often returns +14807689900 or 14807689900),
  // then collapse any formatting noise to bare national digits.
  const national = parseStoredPhoneToNationalNumber(number, cc)
  return reconstructFullPhoneNumber(national, cc)
}

/**
 * Normalize a phone country code so ISO2 (`'us'`, `'CA'`) and ISO3 (`'USA'`, `'CAN'`) variants
 * compare equal. Empty / undefined collapse to `''` so the caller can detect "no country".
 */
const normalizeCountryCode = (cc: string | null | undefined): string => {
  const trimmed = (cc ?? '').trim()
  if (!trimmed) return ''
  const upper = trimmed.toUpperCase()
  // ISO2 (e.g. 'US', 'CA') → ISO3 ('USA', 'CAN'). Fallback: leave longer codes alone.
  return upper.length === 2 ? convertIso2ToIso3(upper).toUpperCase() : upper
}

const phonesMatch = (
  expected: IExpectedProfileSnapshot['phoneHome'],
  actual: IOwnerPhone | undefined,
): boolean => {
  const expectedDigits = normalizePhoneDigits(expected)
  const actualDigits = normalizePhoneDigits(actual)
  if (expectedDigits !== actualDigits) return false
  // Cleared slot: country code is irrelevant when there is no phone number.
  if (!expectedDigits) return true
  // Country must match too — otherwise a USA → MEX (or any cross-country) edit with the same
  // national digits would falsely report "match". Notably matters for NANP (USA/CAN both +1).
  return normalizeCountryCode(expected?.countryCode) === normalizeCountryCode(actual?.countryCode)
}

/**
 * Compares the values the user just submitted to the owner returned by a
 * follow-up `GET /myAccount` and reports whether every changed field actually
 * persisted on the server.
 *
 * Only fields explicitly populated on `expected` are checked, so callers can
 * pass just the fields they know they changed (or pass everything and let the
 * util compare them all). Phone comparison is digit-only and country-aware,
 * so `4807689900` vs `+14807689900` vs `(480) 768-9900` are equivalent.
 */
export function findProfileSaveMismatches(
  expected: IExpectedProfileSnapshot,
  fresh: IOwner | null | undefined,
): IProfileSaveMismatch[] {
  if (!fresh) {
    return [
      {
        field: '__profileFetchFailed',
        expected: '(any)',
        actual: '(profile not loaded)',
      },
    ]
  }
  const mismatches: IProfileSaveMismatch[] = []

  const textFields: Array<{ field: TProfileSaveMismatchField; expected: string | undefined; actual: string | undefined }> = [
    { field: 'emailAddress', expected: expected.emailAddress, actual: fresh.emailAddress },
    { field: 'addressLine1', expected: expected.addressLine1, actual: fresh.addressLine1 },
    { field: 'addressLine2', expected: expected.addressLine2, actual: fresh.addressLine2 },
    { field: 'addressLine3', expected: expected.addressLine3, actual: fresh.addressLine3 },
    { field: 'city', expected: expected.city, actual: fresh.city },
    { field: 'country', expected: expected.country, actual: fresh.country },
    { field: 'stateProvince', expected: expected.stateProvince, actual: fresh.stateProvince },
    { field: 'postalCode', expected: expected.postalCode, actual: fresh.postalCode },
  ]

  for (const { field, expected: e, actual: a } of textFields) {
    if (e === undefined) continue
    if (normalizeText(e) !== normalizeText(a)) {
      mismatches.push({ field, expected: e ?? '', actual: a ?? '' })
    }
  }

  const phoneSlots: Array<{ field: 'phoneHome' | 'phoneMobile' | 'phoneWork'; expected: IExpectedProfileSnapshot['phoneHome']; actual: IOwnerPhone | undefined }> = [
    { field: 'phoneHome', expected: expected.phoneHome, actual: fresh.phoneHome },
    { field: 'phoneMobile', expected: expected.phoneMobile, actual: fresh.phoneMobile },
    { field: 'phoneWork', expected: expected.phoneWork, actual: fresh.phoneWork },
  ]

  for (const { field, expected: e, actual: a } of phoneSlots) {
    if (e === undefined) continue
    if (!phonesMatch(e, a)) {
      mismatches.push({
        field,
        expected: normalizePhoneDigits(e) || '(empty)',
        actual: normalizePhoneDigits(a) || '(empty)',
      })
    }
  }

  return mismatches
}
